import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaBox, FaCalendarAlt, FaHashtag, FaInfoCircle } from 'react-icons/fa';
import ProductService from '../services/ProductService';

const ProductBatchInfo = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [batches, setBatches] = useState([]);
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const productService = new ProductService();

                // Fetch both batches and product details in parallel
                const [batchResult, productResult] = await Promise.all([
                    productService.fetchProductBatches(id),
                    productService.fetchProductDetails(id)
                ]);

                if (batchResult.status) {
                    setBatches(batchResult.data);
                } else {
                    console.error('Batch fetch error:', batchResult.message);
                }

                if (productResult && productResult.length > 0) {
                    setProduct(productResult[0]);
                }

                if (!batchResult.status && (!productResult || productResult.length === 0)) {
                    setError('Failed to load product and batch information.');
                }

            } catch (err) {
                setError('An error occurred while fetching information.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    return (
        <div className="container py-5">
            <div className="d-flex justify-content-between align-items-center mb-4 px-1 px-md-0">
                <button
                    className="btn btn-link text-success p-0 d-flex align-items-center text-decoration-none fw-bold small"
                    onClick={() => navigate(-1)}
                >
                    <FaArrowLeft className="me-2" /> Back
                </button>
                <span className="badge bg-success-soft text-success px-3 py-2" style={{ backgroundColor: 'rgba(25, 135, 84, 0.1)', borderRadius: '10px', fontSize: '0.8rem' }}>
                    Batch Records
                </span>
            </div>

            <div className="card border-0 shadow-lg mb-4" style={{ borderRadius: '15px' }}>
                <div className="card-header bg-success text-white py-3 border-0">
                    <h5 className="mb-0 d-flex align-items-center fw-bold">
                        <FaInfoCircle className="me-2" /> Product Batch Information
                    </h5>
                </div>
                {product && (
                    <div className="card-body border-bottom bg-light py-4 px-4">
                        <div className="row align-items-center">
                            <div className="col-md-auto text-center mb-3 mb-md-0">
                                {product.images && product.images[0] && (
                                    <div className="bg-white rounded p-2 shadow-sm d-inline-block" style={{ border: '1px solid #eee' }}>
                                        <img
                                            src={product.images[0]}
                                            alt={product.product_name}
                                            className="img-fluid"
                                            style={{ maxHeight: '80px', width: '80px', objectFit: 'contain' }}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="col-md ps-md-4 text-center text-md-start">
                                <h3 className="h5 text-success fw-bold mb-0 text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                    {product.product_name}
                                </h3>
                                <p className="text-muted small mb-0 mt-1">Detailed batch tracking and information</p>
                            </div>
                        </div>
                    </div>
                )}
                <div className="card-body p-4">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-success" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3 text-muted">Fetching batch details...</p>
                        </div>
                    ) : error ? (
                        <div className="alert alert-danger" role="alert">
                            {error}
                        </div>
                    ) : batches.length === 0 ? (
                        <div className="text-center py-5">
                            <FaBox size={50} className="text-muted mb-3" />
                            <p className="text-muted">No batch information available for this product.</p>
                        </div>
                    ) : (
                        <>
                            {/* Desktop Table View */}
                            <div className="table-responsive d-none d-md-block border rounded-3">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            {batches.length > 0 && Object.keys(batches[0])
                                                .filter(key => !['batch_id', 'product_id'].includes(key))
                                                .map((key) => (
                                                    <th key={key} className="text-capitalize py-3 px-3 text-nowrap">
                                                        {key.replace(/_/g, ' ')}
                                                    </th>
                                                ))
                                            }
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {batches.map((batch, index) => (
                                            <tr key={batch.batch_id || index}>
                                                {Object.keys(batch)
                                                    .filter(key => !['batch_id', 'product_id'].includes(key))
                                                    .map((key) => (
                                                        <td key={key} className="py-3 px-3 text-nowrap">
                                                            {key === 'batch_no' ? (
                                                                <span className="badge bg-light text-dark border p-2 fw-semibold">
                                                                    {batch[key]}
                                                                </span>
                                                            ) : key === 'expiry' ? (
                                                                <span className="text-danger fw-bold">{batch[key]}</span>
                                                            ) : (
                                                                <span className="text-secondary small">{batch[key]}</span>
                                                            )}
                                                        </td>
                                                    ))
                                                }
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Transposed View (Labels on left, Values on right) */}
                            <div className="d-md-none">
                                {batches.map((batch, index) => (
                                    <div key={index} className="card border-0 shadow-sm mb-4 overflow-hidden" style={{ borderRadius: '12px', border: '1px solid #eee' }}>
                                        <div className="card-header bg-light py-2 px-3 border-bottom-0">
                                            <span className="text-success fw-bold small">Batch Record #{index + 1}</span>
                                        </div>
                                        <table className="table table-sm mb-0 transposed-table">
                                            <tbody>
                                                {Object.keys(batch)
                                                    .filter(key => !['batch_id', 'product_id'].includes(key))
                                                    .map((key) => (
                                                        <tr key={key}>
                                                            <th className="bg-light text-muted small text-capitalize py-3 px-3 border-end" style={{ width: '35%', fontSize: '0.7rem' }}>
                                                                {key.replace(/_/g, ' ')}
                                                            </th>
                                                            <td className="py-3 px-3 small fw-medium">
                                                                {key === 'batch_no' ? (
                                                                    <span className="badge bg-light text-dark border px-2 py-1">{batch[key]}</span>
                                                                ) : key === 'expiry' ? (
                                                                    <span className="text-danger fw-bold">{batch[key]}</span>
                                                                ) : (
                                                                    <div className="text-break text-dark" style={{ lineHeight: '1.4' }}>{batch[key]}</div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <style>{`
                .batch-table-container {
                    transition: transform 0.3s ease;
                }
                .batch-table tr:hover {
                    background-color: rgba(25, 135, 84, 0.05) !important;
                }
                .batch-card {
                    transition: all 0.2s ease-in-out;
                    border: 1px solid rgba(0,0,0,0.05) !important;
                }
                .batch-card:active {
                    transform: scale(0.98);
                }
                .batch-card div:last-child {
                    border-bottom: none !important;
                    margin-bottom: 0 !important;
                    padding-bottom: 0 !important;
                }
                @media (max-width: 768px) {
                    .container {
                        padding-top: 1rem !important;
                        padding-bottom: 1rem !important;
                        padding-left: 0.25rem !important;
                        padding-right: 0.25rem !important;
                    }
                    .card-body {
                        padding: 0.5rem !important;
                        height: auto !important; /* Override global fixed height */
                        min-height: unset !important;
                    }
                    .card {
                        height: auto !important;
                        min-height: unset !important;
                    }
                    .transposed-table {
                        table-layout: fixed !important;
                        width: 100% !important;
                    }
                    .transposed-table th {
                        background-color: #f8f9fa !important;
                        font-weight: 700 !important;
                        white-space: normal !important;
                        word-break: break-word !important;
                    }
                    .transposed-table td {
                        background-color: #ffffff !important;
                        word-break: break-all !important;
                        overflow-wrap: anywhere !important;
                        white-space: normal !important;
                    }
                }
                /* Also override for desktop just in case */
                .card-body {
                    height: auto !important;
                    min-height: unset !important;
                }
                .card {
                    height: auto !important;
                    min-height: unset !important;
                }
            `}</style>
        </div>
    );
};

export default ProductBatchInfo;
